/**
 * Trial onboarding — shared types and validation schemas.
 *
 * Consumed by:
 *   - apps/api/src/routes/trial/*      (server-side validation + response types)
 *   - apps/web/src/pages/Try*.tsx      (client-side request/response shape)
 *   - apps/api/src/services/trial/*    (cookie + kill-switch helpers)
 */
import * as v from 'valibot';
// ---------------------------------------------------------------------------
// Constants (cookie names, sentinel user id, versions)
// ---------------------------------------------------------------------------
/** Name of the signed anonymous-fingerprint cookie (7d). */
export const TRIAL_COOKIE_FINGERPRINT_NAME = 'sam_trial_fingerprint';
/** Name of the signed claim-token cookie (48h). */
export const TRIAL_COOKIE_CLAIM_NAME = 'sam_trial_claim';
/**
 * Sentinel user id that owns anonymous trial projects until claim.
 * Seeded into `users` by migration 0043 with email
 * `anonymous-trials@simple-agent-manager.internal`.
 */
export const TRIAL_ANONYMOUS_USER_ID = 'system_anonymous_trials';
// ---------------------------------------------------------------------------
// Valibot schemas
// ---------------------------------------------------------------------------
/**
 * GitHub public-repo URL pattern:
 *   https://github.com/<owner>/<repo>
 * - Owner: 1–39 chars, alphanumeric + hyphen, cannot start/end with hyphen
 * - Repo: 1–100 chars, alphanumeric + '-', '_', '.'
 * - Optional trailing slash, `.git` suffix, or `/tree/...` segment is stripped upstream.
 */
const GITHUB_REPO_URL_REGEX = /^https:\/\/github\.com\/[A-Za-z0-9](?:[A-Za-z0-9-]{0,37}[A-Za-z0-9])?\/[A-Za-z0-9_.-]{1,100}(?:\.git)?\/?$/;
export const TrialCreateRequestSchema = v.object({
    repoUrl: v.pipe(v.string(), v.trim(), v.regex(GITHUB_REPO_URL_REGEX, 'Must be a public GitHub repository URL')),
});
export const TrialWaitlistRequestSchema = v.object({
    email: v.pipe(v.string(), v.trim(), v.email()),
});
export const TrialEventSchema = v.variant('type', [
    v.object({
        type: v.literal('trial.started'),
        trialId: v.string(),
        projectId: v.string(),
        repoUrl: v.string(),
        startedAt: v.number(),
    }),
    v.object({
        type: v.literal('trial.progress'),
        stage: v.optional(v.string()),
        message: v.optional(v.string()),
        progress: v.optional(v.number()),
        at: v.number(),
    }),
    v.object({
        type: v.literal('trial.knowledge'),
        entity: v.string(),
        observation: v.string(),
        at: v.number(),
    }),
    v.object({
        type: v.literal('trial.idea'),
        ideaId: v.string(),
        title: v.string(),
        summary: v.string(),
        at: v.number(),
    }),
    v.object({
        type: v.literal('trial.ready'),
        trialId: v.optional(v.string()),
        projectId: v.string(),
        workspaceUrl: v.optional(v.string()),
        at: v.number(),
    }),
    v.object({
        type: v.literal('trial.agent_activity'),
        role: v.picklist(['assistant', 'tool', 'thinking']),
        text: v.string(),
        toolName: v.optional(v.string()),
        at: v.number(),
    }),
    v.object({
        type: v.literal('trial.error'),
        error: v.string(),
        message: v.string(),
        at: v.number(),
    }),
]);
export function parseTrialEvent(value) {
    return v.parse(TrialEventSchema, value);
}
//# sourceMappingURL=trial.js.map